// v2 campaign lane file — see app/v2/README.md. Owned by one build agent at a time;
// other agents and humans: do not edit. Routes here are HIDDEN (reachable only via
// /v2-screens and deep links) and every element must wrap in <RequireAuth>.
import { lazy, type ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { RequireAuth } from '@/shared/auth/RequireAuth';

const ReceiptMaintenance2Page = lazy(
  () => import('@/features/receipts/maintenance2/ReceiptMaintenance2Page'),
);
const ReceiveProduct2Page = lazy(
  () => import('@/features/receipts/receiveproduct2/ReceiveProduct2Page'),
);
const RcvPaSortByZone2Page = lazy(
  () => import('@/features/putawaysort/rcvbyzone2/RcvPaSortByZone2Page'),
);
const TrailerBoard2Page = lazy(
  () => import('@/features/yard/trailers2/TrailerBoard2Page'),
);
// rank #38 lives under features/truck/ (not yard/) because the faithful family it reuses is
// features/truck/inboundsearch — features/yard/* → features/truck/* is a cross-DOMAIN import
// and a dep-cruiser error (ADR-0012). The ROUTE is /yard/inbound-trucks2 either way; see the
// page header's DEVIATION #2.
const DockBoard2Page = lazy(() => import('@/features/truck/inbound2/DockBoard2Page'));
const AutoRestock2Page = lazy(
  () => import('@/features/restock/autorestock2/AutoRestock2Page'),
);
const PutawayErrors2Page = lazy(
  () => import('@/features/workpool/putawayerrors2/PutawayErrors2Page'),
);
// rank #36 lives under features/restock/ (its faithful family is the rstk-requests browse cfg
// plus the restock write controller); the ROUTE is /restock/requests2.
const RstkRequests2Page = lazy(
  () => import('@/features/restock/requests2/RstkRequests2Page'),
);
// slice-3689 (Chris, docs/demochanges.txt 2026-09-17 — the demo "New Screens" slider needs a
// Location Search v2 and none existed). NOT on v2_wins.html; catalog rank 51, after the 50.
const LocationSearch2Page = lazy(
  () => import('@/features/location/search2/LocationSearch2Page'),
);

export const slot3Routes: ReactElement[] = [
  // rank #2 — Receipt Maintenance. Bare (status2-style lookup card) + the keyed form, so
  // /v2-screens can always link it and a drill can deep-link a receipt.
  <Route
    key="/receipts/maintenance2"
    path="/receipts/maintenance2"
    element={
      <RequireAuth>
        <ReceiptMaintenance2Page />
      </RequireAuth>
    }
  />,
  <Route
    key="/receipts/maintenance2/:po/:releaseNum"
    path="/receipts/maintenance2/:po/:releaseNum"
    element={
      <RequireAuth>
        <ReceiptMaintenance2Page />
      </RequireAuth>
    }
  />,
  // rank #11 — Receive Product (scan station).
  <Route
    key="/receipts/receive-product2"
    path="/receipts/receive-product2"
    element={
      <RequireAuth>
        <ReceiveProduct2Page />
      </RequireAuth>
    }
  />,
  // rank #37 — Recv Putaway Sort by Zone (receiving station).
  <Route
    key="/putaway-sort/rcv-by-zone2"
    path="/putaway-sort/rcv-by-zone2"
    element={
      <RequireAuth>
        <RcvPaSortByZone2Page />
      </RequireAuth>
    }
  />,
  // rank #33 — Trailer Search as a yard status board. Bare route; ?view= picks the rail
  // section and an OPTIONAL ?trailer= seeds the Trailer criterion for the #38 cross-link.
  <Route
    key="/yard/trailer-search2"
    path="/yard/trailer-search2"
    element={
      <RequireAuth>
        <TrailerBoard2Page />
      </RequireAuth>
    }
  />,
  // rank #38 — Inbound Truck Search as a dock queue board.
  <Route
    key="/yard/inbound-trucks2"
    path="/yard/inbound-trucks2"
    element={
      <RequireAuth>
        <DockBoard2Page />
      </RequireAuth>
    }
  />,
  // rank #31 — Automatic Restocking as a stock-health board. Bare route; ?view= picks the
  // urgency section (criteria / stockouts / below-min / below-optimal / all).
  <Route
    key="/restock/auto-restock2"
    path="/restock/auto-restock2"
    element={
      <RequireAuth>
        <AutoRestock2Page />
      </RequireAuth>
    }
  />,
  // rank #32 — Putaway Errors grouped by failure reason.
  <Route
    key="/putaway-errors2"
    path="/putaway-errors2"
    element={
      <RequireAuth>
        <PutawayErrors2Page />
      </RequireAuth>
    }
  />,
  // rank #36 — Restock Requests as a queue board over the embedded rstk-requests browse.
  <Route
    key="/restock/requests2"
    path="/restock/requests2"
    element={
      <RequireAuth>
        <RstkRequests2Page />
      </RequireAuth>
    }
  />,
  // slice-3689 — Location Search (PB w_location_search). Mirrors the faithful route's own
  // shape: /browse/cfg-locations → /browse/cfg-locations2. Bare route only; the window is
  // query-to-load and takes no params, so /v2-screens can always link it.
  <Route
    key="/browse/cfg-locations2"
    path="/browse/cfg-locations2"
    element={
      <RequireAuth>
        <LocationSearch2Page />
      </RequireAuth>
    }
  />,
];
